import {
  faArrowLeft,
  faCheckCircle,
  faPlus,
  faTachometer,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Scroll from "react-scroll";

import { DateTime } from "luxon";
import toast from "react-hot-toast";
import { postPromoCodeCreateAPI } from "../../API/PromoCode";
import FormErrorBox from "../../Components/Reusable/FormErrorBox";
import FormInputField from "../../Components/Reusable/FormInputField";
import FormUnixDateField from "../../Components/Reusable/FormUnixDateField";
import PageLoadingContent from "../../Components/Reusable/PageLoadingContent";
import { useAuthStore } from "../../stores/auth-store";

function AddOrgPromoCode() {
  const user = useAuthStore((store) => store.user);
  const [errors, setErrors] = useState({});
  const [forceURL, setForceURL] = useState("");
  const [isFetching, setFetching] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    limit: null,
    endDate: null,
    discount: null,
    startDate: DateTime.fromISO(new Date().toISOString()).toFormat(
      "yyyy-MM-dd"
    ),
  });

  function onUserAddSuccess(response) {
    toast.success("Promo Code Created");
    setForceURL("/org-promo-code");
  }

  function onUserAddError(apiErr) {
    toast.error(apiErr.message);

    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onUserAddDone() {
    setFetching(false);
  }

  const onSubmitClick = (e) => {
    setErrors({});
    setFetching(true);

    const postData = {
      ...formData,
      name: formData.name,
      usageLimit: parseInt(formData.limit),
      discount: parseInt(formData.discount),
      endDate: DateTime.fromSeconds(formData.endDate).toFormat("yyyy-MM-dd"),
      organizationId: user?.organizationId,
      userId: user?.id,
    };

    postPromoCodeCreateAPI(
      postData,
      onUserAddSuccess,
      onUserAddError,
      onUserAddDone
    );
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      window.scrollTo(0, 0);
    }

    return () => {
      mounted = false;
    };
  }, []);

  if (forceURL !== "") {
    return <Navigate to={forceURL} />;
  }

  return (
    <>
      <div className="container">
        <section className="section">
          {/* Desktop Breadcrumbs */}
          <nav className="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
            <ul>
              <li>
                <Link to="/org-dashboard">
                  <FontAwesomeIcon className="fas" icon={faTachometer} />
                  &nbsp;Dashboard
                </Link>
              </li>
              <li className="">
                <Link to="/org-promo-code" aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faTicket} />
                  &nbsp;Promo Code
                </Link>
              </li>
              <li className="is-active">
                <Link aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faPlus} />
                  &nbsp;New Promo Code
                </Link>
              </li>
            </ul>
          </nav>

          {/* Mobile Breadcrumbs */}
          <nav
            className="breadcrumb is-hidden-desktop"
            aria-label="breadcrumbs"
          >
            <ul>
              <li className="">
                <Link to={`/org-promo-code`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                  &nbsp;Back to Promo Code
                </Link>
              </li>
            </ul>
          </nav>

          {/* Page */}
          <nav className="box">
            <p className="title is-4">
              <FontAwesomeIcon className="fas" icon={faPlus} />
              &nbsp;New Promo Code
            </p>
            {isFetching ? (
              <PageLoadingContent displayMessage={"Please wait"} />
            ) : (
              <>
                <FormErrorBox errors={errors} />
                <div className="fixed-grid">
                  <div className="grid">
                    <FormInputField
                      name="Name"
                      placeholder="Promo Code"
                      className="cell"
                      errorText={errors && errors.name}
                      type="text"
                      maxLength={5}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          name: e.target.value,
                        }))
                      }
                      maxWidth="350px"
                    />

                    <FormInputField
                      label="Description"
                      name="Description"
                      placeholder="Description"
                      className="cell"
                      errorText={errors && errors.description}
                      type="text"
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          description: e.target.value,
                        }))
                      }
                      isRequired={true}
                      maxWidth="350px"
                    />
                    <FormInputField
                      label="Limit of Usage"
                      name="Limit"
                      className="cell"
                      placeholder="Limit of Usage"
                      errorText={errors && errors.limit}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          limit: e.target.value,
                        }))
                      }
                      type="number"
                      isRequired={true}
                      maxWidth="350px"
                    />
                    <FormInputField
                      label="Discount"
                      name="Discount"
                      className="cell"
                      placeholder="Discount Percentage"
                      errorText={errors && errors.discount}
                      onChange={(e) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          discount: e.target.value,
                        }))
                      }
                      type="number"
                      isRequired={true}
                      maxWidth="350px"
                    />
                    <FormUnixDateField
                      label="Expiry Date"
                      name="Expiry Date"
                      className="cell"
                      placeholder="Expiry Date"
                      value={formData.endDate}
                      errorText={errors && errors.endDate}
                      onChange={(d) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          endDate: d,
                        }))
                      }
                      isRequired={true}
                      maxWidth="350px"
                    />
                  </div>
                </div>
                <div className="columns">
                  <div className="column">
                    <div className="columns">
                      <div className="column is-half">
                        <Link
                          to={`/promo-code`}
                          className="button is-hidden-touch"
                        >
                          <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                          &nbsp;Back
                        </Link>
                        <Link
                          to={`/promo-code`}
                          className="button is-fullwidth is-hidden-desktop"
                        >
                          <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                          &nbsp;Back
                        </Link>
                      </div>
                      <div className="column is-half has-text-right">
                        <button
                          onClick={onSubmitClick}
                          className="button is-primary is-hidden-touch"
                        >
                          <FontAwesomeIcon
                            className="fas"
                            icon={faCheckCircle}
                          />
                          &nbsp;Save
                        </button>
                        <button
                          onClick={onSubmitClick}
                          className="button is-primary is-fullwidth is-hidden-desktop"
                        >
                          <FontAwesomeIcon
                            className="fas"
                            icon={faCheckCircle}
                          />
                          &nbsp;Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </nav>
        </section>
      </div>
    </>
  );
}

export default AddOrgPromoCode;
